import React, { useState, useEffect } from "react";
import axios from "axios";
import { apiurl } from "../config/config";

const TwoFactorToggle = ({ user }) => {
  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    mode: "otp", // default
    approvalBy: [],
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedMode, setSelectedMode] = useState("otp");
  const [approvalUsers, setApprovalUsers] = useState([]); // array of user IDs

  // Load initial state from user
  useEffect(() => {
    if (user?.twoFactorAuth) {
      setTwoFactor({
        enabled: user.twoFactorAuth.enabled,
        mode: user.twoFactorAuth.mode || "otp",
        approvalBy: user.twoFactorAuth.approvalBy || [],
      });
      setSelectedMode(user.twoFactorAuth.mode || "otp");
      setApprovalUsers(user.twoFactorAuth.approvalBy || []);
    }
  }, [user]);

  const handleToggle = async () => {
    if (!user) return;
    setLoading(true);
    setMessage("");

    try {
      const payload = {
        enable: !twoFactor.enabled,
        userObjId: user._id,
        mode: selectedMode,
      };

      if (selectedMode === "otp") {
        payload.email = user.email;
      } else if (selectedMode === "approval") {
        payload.approvalBy = approvalUsers; // must be array of user IDs
      }

      const res = await axios.post(`${apiurl}/api/user/toggle-2fa`, payload);

      if (res.data.success) {
        setTwoFactor(res.data.twoFactorAuth);
        setMessage(
          !twoFactor.enabled
            ? "Two-Factor Authentication enabled successfully."
            : "Two-Factor Authentication disabled successfully."
        );
      } else {
        setMessage("Failed to update Two-Factor Authentication.");
      }
    } catch (err) {
      console.error(err);
      setMessage("Error while updating Two-Factor Authentication.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md my-3 border">
      <h3 className="text-xl font-medium mb-4">Two-Factor Authentication (2FA)</h3>

      <div className="flex items-center gap-6 mb-4">
        <span
          className={`font-semibold ${
            twoFactor.enabled ? "text-green-600" : "text-red-600"
          }`}
        >
          {twoFactor.enabled ? "Enabled" : "Disabled"}
        </span>
        <button
          onClick={handleToggle}
          disabled={loading}
          className={`px-4 py-2 rounded-md text-white ${
            twoFactor.enabled ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
          }`}
        >
          {loading ? "Processing..." : twoFactor.enabled ? "Disable 2FA" : "Enable 2FA"}
        </button>
      </div>

      {/* Mode selector */}
      {!twoFactor.enabled && (
        <div className="flex items-center gap-4 mb-2">
          <label>
            <input
              type="radio"
              value="otp"
              checked={selectedMode === "otp"}
              onChange={() => setSelectedMode("otp")}
            />{" "}
            OTP
          </label>
          <label>
            <input
              type="radio"
              value="approval"
              checked={selectedMode === "approval"}
              onChange={() => setSelectedMode("approval")}
            />{" "}
            Approval
          </label>
        </div>
      )}

      {/* Approval selection */}
      {selectedMode === "approval" && !twoFactor.enabled && (
        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Select approvers:</label>
          <input
            type="text"
            placeholder="Enter approver IDs comma separated"
            value={approvalUsers.join(",")}
            onChange={(e) =>
              setApprovalUsers(e.target.value.split(",").map((id) => id.trim()))
            }
            className="w-full px-3 py-2 border rounded-md"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter user IDs who can approve logins.
          </p>
        </div>
      )}

      {message && <p className="text-sm text-gray-600 mt-2">{message}</p>}
    </div>
  );
};

export default TwoFactorToggle;
